import { collect } from 'collect.js';
import moment from 'moment-timezone';

/**
 * Pure order-money / order-time calculations extracted from order components so
 * they can be unit-tested in isolation. See tests/services/helpers/OrderMath.test.js.
 */

/**
 * Build the nested modifier tree for a line item, resolving each modifier's
 * *effective* quantity (its own quantity multiplied by every ancestor's, all the
 * way up to the line-item quantity). Mirrors the recursive grouping the order
 * detail view renders.
 *
 * @returns Array<{ id, name, quantity, price, children: [...] }>
 */
export function computeModifierTree(lineItem) {
  const byParentId = collect(lineItem?.modifiers ?? []).groupBy('parent_id');

  const build = (modifier, parentQuantity) => {
    const quantity = modifier.quantity * parentQuantity;
    const children = byParentId.get(modifier.id);

    return {
      id: modifier.id,
      name: modifier.modifier?.name,
      quantity,
      price: modifier.package_price,
      children: children ? children.map((child) => build(child, quantity)).all() : [],
    };
  };

  const roots = byParentId.get('');

  return roots ? roots.map((root) => build(root, lineItem.quantity)).all() : [];
}

/**
 * Normalize an order's discount breakdown for display: amounts are stored in
 * minor units (cents) and rendered divided by 100. Returns null when there is
 * nothing to show.
 *
 * @returns { total, currency, items: Array<{ key, amount }> } | null
 */
export function normalizeDiscountBreakdown(order) {
  const breakdown = order?.discount_breakdown;

  if (!breakdown) {
    return null;
  }

  const entries = Object.entries(breakdown);

  if (!entries.length) {
    return null;
  }

  return {
    total: order?.total_discount,
    currency: order?.currency,
    items: entries.map(([key, value]) => ({ key, amount: value / 100 })),
  };
}

/**
 * Minute delta between the actual and the initially-estimated delivery time,
 * with seconds zeroed so partial minutes don't skew the result. Negative = early,
 * positive = late.
 */
export function deliveryTimeDifferenceMinutes(initial, actual) {
  return moment(actual).set('second', 0).diff(moment(initial).set('second', 0), 'minutes');
}
